/**
 * AdminLanding - Admin portal entry page
 * Route: /admin
 * Provides clear access links to Analytics and Reports panels
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart2, AlertTriangle, Lock, Shield } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';

export default function AdminLanding() {
  const navigate = useNavigate();
  const { language } = useApp();

  const labels = {
    es: {
      title: 'Panel de Administración',
      subtitle: 'Acceso restringido — solo para administradores de DexRiddle',
      analytics: 'Analytics',
      analyticsDesc: 'Dashboard BI con tráfico, usuarios, Pokémon meta y salud del sistema en tiempo real.',
      reports: 'Reportes de Errores',
      reportsDesc: 'Gestiona los reportes de bugs enviados por los usuarios. Márcalos como resueltos o elimínalos.',
      enter: 'Acceder',
      warning: 'Requiere contraseña de administrador',
    },
    en: {
      title: 'Admin Panel',
      subtitle: 'Restricted access — DexRiddle administrators only',
      analytics: 'Analytics',
      analyticsDesc: 'BI dashboard with real-time traffic, users, Pokémon meta and system health.',
      reports: 'Bug Reports',
      reportsDesc: 'Manage bug reports submitted by users. Mark as resolved or delete them.',
      enter: 'Enter',
      warning: 'Requires admin password',
    },
  };

  const t = labels[language] || labels.en;

  const sections = [
    {
      icon: BarChart2,
      iconColor: 'text-violet-500',
      label: t.analytics,
      desc: t.analyticsDesc,
      path: '/admin/analytics',
      gradient: 'from-violet-600 to-indigo-600',
      bgClass: 'bg-violet-500/10 border-violet-500/30',
      hoverBorder: 'hover:border-violet-500',
    },
    {
      icon: AlertTriangle,
      iconColor: 'text-rose-500',
      label: t.reports,
      desc: t.reportsDesc,
      path: '/admin/reports',
      gradient: 'from-rose-500 to-amber-500',
      bgClass: 'bg-rose-500/10 border-rose-500/30',
      hoverBorder: 'hover:border-rose-500',
    },
  ];

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-rose-500 flex items-center justify-center mb-4 shadow-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            <span className="text-violet-600 dark:text-violet-400">Dex</span>
            <span className="text-rose-500 dark:text-rose-400">Riddle</span>
            {' '}
            <span className="text-foreground">{t.title}</span>
          </h1>
          <p className="text-muted-foreground text-sm">{t.subtitle}</p>
        </motion.div>

        {/* Access Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {sections.map(({ icon: Icon, iconColor, label, desc, path, gradient, bgClass, hoverBorder }, i) => (
            <motion.div
              key={path}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card
                className={`cursor-pointer border transition-all duration-200 ${hoverBorder} hover:shadow-lg`}
                onClick={() => navigate(path)}
                data-testid={`admin-link-${path.replace('/admin/', '')}`}
              >
                <CardContent className="p-6 flex flex-col gap-4">
                  {/* Icon badge */}
                  <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${bgClass}`}>
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                  </div>

                  {/* Text */}
                  <div>
                    <h2 className="text-lg font-bold mb-1">{label}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                  </div>

                  {/* Footer row */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Lock className="w-3 h-3" />
                      <span>{t.warning}</span>
                    </div>
                    <button
                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r ${gradient} hover:opacity-90 transition-opacity`}
                      onClick={(e) => { e.stopPropagation(); navigate(path); }}
                    >
                      {t.enter} →
                    </button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-xs text-muted-foreground mt-8"
        >
          {language === 'es'
            ? 'Si no tienes credenciales de acceso, contacta al administrador del sitio.'
            : 'If you do not have access credentials, contact the site administrator.'}
        </motion.p>
      </div>
    </div>
  );
}
